import React from "react";
import { useTranslation } from "react-i18next";

import { TranslatedStandard } from "types";
import { Directive } from "pages/Backoffice/Questionaire/types";
import { useAIStandardResearch } from "hooks/useAIStandardResearch";
import { AIStandardSuggestion } from "./types";
import { SuggestionCard } from "./SuggestionCard";

function mapSuggestionToTranslatedStandard(
  suggestion: AIStandardSuggestion
): TranslatedStandard {
  return {
    id: suggestion.standard_id,
    name: suggestion.standard_id,
    type: suggestion.type,
    latest: suggestion.standard_version,
    content: suggestion.title,
    versions: suggestion.standard_version ? [suggestion.standard_version] : [],
    retracted: false,
    directive: suggestion.directive as Directive,
    lastRecorded: "",
  };
}

interface AIStandardResearchProps {
  projectId: string;
  selectedStandards: string[];
  onSelect: (s: TranslatedStandard) => void;
  suggestions: AIStandardSuggestion[];
  setSuggestions: (s: AIStandardSuggestion[]) => void;
  summary: string;
  setSummary: (s: string) => void;
  dismissedIds: string[];
  setDismissedIds: (ids: string[]) => void;
  setIsLoading: (loading: boolean) => void;
}

export const AIStandardResearch = ({
  projectId,
  selectedStandards,
  onSelect,
  suggestions,
  setSuggestions,
  summary,
  setSummary,
  dismissedIds,
  setDismissedIds,
  setIsLoading,
}: AIStandardResearchProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState("");

  const { mutate, isLoading, isError } = useAIStandardResearch(projectId);

  const isQueryValid = query.trim().length >= 3;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isQueryValid || isLoading) return;

    const request = { query: query.trim() };

    setIsLoading(true);
    mutate(request, {
      onSuccess: (data) => {
        setSuggestions(data.suggestions);
        setSummary(data.summary);
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const handleAdd = (suggestion: AIStandardSuggestion) => {
    const standard = mapSuggestionToTranslatedStandard(suggestion);
    onSelect(standard);
  };

  const handleDismiss = (standardId: string) => {
    setDismissedIds([...dismissedIds, standardId]);
  };

  const visibleSuggestions = suggestions.filter(
    (s) => !dismissedIds.includes(s.standard_id)
  );

  return (
    <div>
      <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate mx-4 mt-4 mb-1">
        {t("standards.aiResearch.headline")}
      </h1>
      <p className="text-sm text-gray-600 leading-relaxed mx-4 mb-4">
        {t("standards.aiResearch.subline")}
      </p>

      <div className="mx-4 mb-4">
        <form onSubmit={handleSubmit}>
          <textarea
            rows={3}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isLoading}
            placeholder={t("standards.aiResearch.inputPlaceholder")}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm disabled:opacity-60 disabled:cursor-not-allowed"
          />
          {query.length > 0 && !isQueryValid && (
            <p className="mt-1 text-xs text-red-500">
              {t("standards.aiResearch.minChars")}
            </p>
          )}
          <div className="mt-2">
            <button
              type="submit"
              disabled={!isQueryValid || isLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading
                ? t("standards.aiResearch.searching")
                : t("standards.aiResearch.searchButton")}
            </button>
          </div>
        </form>
      </div>

      {isLoading && (
        <div className="mx-4 mb-4 rounded-lg border border-cyan-200 bg-cyan-50 p-4">
          <ul className="space-y-2">
            {(
              [
                "standards.aiResearch.loading.analyzing",
                "standards.aiResearch.loading.searching",
                "standards.aiResearch.loading.generating",
              ] as const
            ).map((key) => (
              <li key={key} className="flex items-center gap-2 animate-pulse">
                <svg
                  className="h-4 w-4 text-cyan-600 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                <span className="text-sm text-cyan-700">{t(key)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isError && !isLoading && (
        <div className="mx-4 mb-4 rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-700">{t("standards.aiResearch.error")}</p>
        </div>
      )}

      {!isLoading && suggestions.length > 0 && (
        <div className="mx-4">
          {summary && (
            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                {t("standards.aiResearch.summary")}
              </p>
              <p className="text-sm text-gray-700">{summary}</p>
            </div>
          )}

          <h2 className="text-sm font-medium text-gray-900 mb-3">
            {t("standards.aiResearch.results")} ({visibleSuggestions.length})
          </h2>

          {visibleSuggestions.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t("standards.aiResearch.noResults")}
            </p>
          ) : (
            <ul className="space-y-3">
              {visibleSuggestions.map((suggestion) => (
                <li key={suggestion.standard_id}>
                  <SuggestionCard
                    suggestion={suggestion}
                    isSelected={selectedStandards.some(
                      (s) => s.split(":")[0] === suggestion.standard_id
                    )}
                    onAdd={handleAdd}
                    onDismiss={handleDismiss}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
